import type { DeckCardThumbnailSummary } from "@/cards/common/types/app-deck/deckCardThumbnail.types";
import { parseSealDeadline } from "@/cards/common/utils/useSealTimeGate";
import CardBorder from "@/modules/borders/CardBorder";
import { DeckCardKakaoKind } from "@/modules/kakao/deckCard/deckCardKakaoKind";
import { useAuth } from "@/shared";
import { ROUTES } from "@/shared/constants";
import {
  CARD_ASPECT_RATIO_CSS,
  CARD_BACK_BG,
  CARD_BORDER_RADIUS,
  cardBackScrollSx,
  scaledCardPx,
} from "@/shared/constants/cardLayout";
import { Share } from "@mui/icons-material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import { Avatar, Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CardEngagementAccordion from "./CardEngagementAccordion";
import CardEngagementPanel from "./CardEngagementPanel";
import { DownloadIcon } from "lucide-react";

type DeckCardExpandBackFaceProps = {
  cardId?: number;
  summary?: DeckCardThumbnailSummary | null;
  talentId?: number;
  talentName?: string;
  talentAvatarUrl?: string;
  cardTitle?: string;
  disableScroll?: boolean;
  isLoggedIn?: boolean;
  isPurchased?: boolean;
  isOwner?: boolean;
  onSignInClick?: () => void;
  onPurchaseClick?: () => void;
  onShare?: () => void;
  shareKind?: DeckCardKakaoKind;
  description?: string;
  shareable?: boolean;
  tableOfContents?: Array<Record<string, unknown>>;
  contentExps?: Array<Record<string, unknown>>;
  sealTime?: string | number | null;
  /** Wrap the back face in NeonCard (matches thumbnail `addBorder`). */
  addBorder?: boolean;
  purchaseId?: number | string | null;
  /** Called when the user clicks "Download card". */
  onDownloadClick?: () => void;
  /** True while the card image is being prepared. */
  isDownloading?: boolean;
};

const sectionLabelSx = {
  font: "var(--semibold-xxs)",
  letterSpacing: "0.12em",
  textTransform: "uppercase" as const,
  color: "#807356",
  pb: "var(--spacing-2x)",
  mb: 0.85,
};

const benefitTileSx = {
  py: "var(--spacing-3x)",
  px: "var(--spacing-2x)",
  borderRadius: 2,
  bgcolor: "#241D13",
};

const DeckCardExpandBackFace: React.FC<DeckCardExpandBackFaceProps> = ({
  cardId,
  summary,
  talentId,
  talentName,
  talentAvatarUrl,
  disableScroll = false,
  isLoggedIn = true,
  isPurchased = true,
  isOwner = false,
  onSignInClick,
  onPurchaseClick,
  onShare,
  shareKind = "single",
  description,
  shareable = true,
  tableOfContents = [],
  contentExps = [],
  sealTime,
  addBorder = false,
  purchaseId,
  onDownloadClick,
  isDownloading = false,
}) => {
  const { t, i18n } = useTranslation("taascard");
  const { userInfo } = useAuth();
  const navigate = useNavigate();

  const CARD_INFO: Record<
    DeckCardKakaoKind,
    {
      labelKey: string;
      subtitleKey: string;
      benefits: { icon: string; labelKey: string; textKey: string }[];
    }
  > = {
    single: {
      labelKey: "deck_single_label",
      subtitleKey: "deck_single_unlock_subtitle",
      benefits: [
        {
          icon: "🎵",
          labelKey: "deck_single_benefit_music_label",
          textKey: "deck_single_benefit_music_text",
        },
        {
          icon: "🖼️",
          labelKey: "deck_single_benefit_visuals_label",
          textKey: "deck_single_benefit_visuals_text",
        },
        {
          icon: "✉️",
          labelKey: "deck_single_benefit_photocards_label",
          textKey: "deck_single_benefit_photocards_text",
        },
      ],
    },
    demo: {
      labelKey: "deck_demo_label",
      subtitleKey: "deck_demo_unlock_subtitle",
      benefits: [
        {
          icon: "🎬",
          labelKey: "deck_demo_benefit_sneak_label",
          textKey: "deck_demo_benefit_sneak_text",
        },
        {
          icon: "🎙️",
          labelKey: "deck_demo_benefit_bts_label",
          textKey: "deck_demo_benefit_bts_text",
        },
        {
          icon: "⚡",
          labelKey: "deck_demo_benefit_early_label",
          textKey: "deck_demo_benefit_early_text",
        },
      ],
    },
    season: {
      labelKey: "deck_season_label",
      subtitleKey: "deck_summary_season_tagline",
      benefits: [
        {
          icon: "🗺️",
          labelKey: "deck_season_benefit_journey_label",
          textKey: "deck_season_benefit_journey_text",
        },
        {
          icon: "📚",
          labelKey: "deck_season_benefit_series_label",
          textKey: "deck_season_benefit_series_text",
        },
        {
          icon: "🔓",
          labelKey: "deck_season_benefit_unlock_label",
          textKey: "deck_season_benefit_unlock_text",
        },
      ],
    },
    collection: {
      labelKey: "deck_collection_label",
      subtitleKey: "deck_collection_unlock_subtitle",
      benefits: [
        {
          icon: "🎉",
          labelKey: "deck_collection_benefit_content_label",
          textKey: "deck_collection_benefit_content_text",
        },
      ],
    },
    impact: {
      labelKey: "deck_impact_label",
      subtitleKey: "deck_impact_unlock_subtitle",
      benefits: [
        {
          icon: "💚",
          labelKey: "deck_impact_benefit_cause_label",
          textKey: "deck_impact_benefit_cause_text",
        },
        {
          icon: "📖",
          labelKey: "deck_impact_benefit_story_label",
          textKey: "deck_impact_benefit_story_text",
        },
      ],
    },
  };

  const scale = 1;
  const cardInfo = CARD_INFO[shareKind] ?? CARD_INFO.single;
  const isCollection = shareKind === "collection";
  const showPreviewSections = !isCollection;
  const showLivingPlan = shareKind === "season" || shareKind === "demo" || shareKind === "single";
  const canViewEngagement = isOwner || isPurchased;
  const talentDisplayName = talentName ?? t("talent");

  const livingPlan = useMemo(() => {
    if (!showLivingPlan) return [];

    const dateFields = ["release_date", "created_at", "updated_at", "start_time"] as const;
    const getItemTime = (item: Record<string, unknown>) => {
      for (const field of dateFields) {
        const value = item[field];
        if (typeof value !== "string" && typeof value !== "number") continue;
        const time =
          parseSealDeadline(value) ??
          new Date(typeof value === "number" ? value : String(value)).getTime();
        if (Number.isFinite(time)) return time;
      }
      return null;
    };
    const formatDate = (time: number) =>
      new Intl.DateTimeFormat(i18n.resolvedLanguage || i18n.language, {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(new Date(time));

    const itemKind = shareKind === "season" ? "series" : "episodes";
    const itemCount = tableOfContents.length + contentExps.length;
    const dateSource = contentExps.length > 0 ? contentExps : tableOfContents;
    const newestTime = dateSource.reduce<number | null>((newest, item) => {
      const time = getItemTime(item);
      return time != null && (newest == null || time > newest) ? time : newest;
    }, null);
    const items: Array<{ icon: string; title: string; subtitle: string }> = [];

    if (itemCount > 0) {
      items.push({
        icon: "📅",
        title:
          newestTime != null
            ? t(
                itemKind === "series"
                  ? "deck_summary_new_series_on"
                  : "deck_summary_new_episodes_on",
                { date: formatDate(newestTime) }
              )
            : t(
                itemKind === "series" ? "deck_summary_series_added" : "deck_summary_episodes_added",
                { count: itemCount }
              ),
        subtitle: t(
          itemKind === "series" ? "deck_summary_series_added" : "deck_summary_episodes_added",
          { count: itemCount }
        ),
      });
    }

    if (sealTime != null && sealTime !== "") {
      const sealTimestamp = parseSealDeadline(sealTime);
      if (sealTimestamp != null) {
        items.push({
          icon: "🔒",
          title: t("deck_summary_seals_on", { date: formatDate(sealTimestamp) }),
          subtitle: t(
            itemKind === "series"
              ? "deck_summary_after_final_series"
              : "deck_summary_after_final_episodes"
          ),
        });
      }
    }

    return items;
  }, [
    contentExps,
    i18n.language,
    i18n.resolvedLanguage,
    sealTime,
    shareKind,
    showLivingPlan,
    t,
    tableOfContents,
  ]);

  const cardSubtitle = useMemo(() => {
    if (description?.trim()) return description.trim();
    if (shareKind === "season") {
      return t("deck_summary_season_tagline", { talentName: talentDisplayName });
    }
    return t(cardInfo.subtitleKey);
  }, [cardInfo.subtitleKey, description, shareKind, t, talentDisplayName]);

  const handleGoToHall = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!talentId) return;
    navigate(`${ROUTES.TALENT_HALL}/${talentId}`);
  };

  const renderBenefitTile = (item: { icon: string; labelKey: string; textKey: string }) => (
    <Box key={item.labelKey} sx={benefitTileSx}>
      <Typography sx={{ font: "var(--semibold-xs)", color: "var(--card-dark-text)" }}>
        {item.icon} {t(item.labelKey)}
      </Typography>
      <Typography
        sx={{
          mt: "var(--spacing-1x)",
          font: "var(--body-xs)",
          color: "#807356",
          lineHeight: 1.45,
        }}
      >
        {t(item.textKey)}
      </Typography>
    </Box>
  );

  const renderLivingPlanTile = (item: (typeof livingPlan)[number]) => (
    <Box key={`${item.icon}-${item.title}`} sx={benefitTileSx}>
      <Typography sx={{ font: "var(--semibold-xs)", color: "var(--card-dark-text)" }}>
        {item.icon} {item.title}
      </Typography>
      <Typography
        sx={{
          mt: "var(--spacing-1x)",
          font: "var(--body-xs)",
          color: "#807356",
          lineHeight: 1.45,
        }}
      >
        {item.subtitle}
      </Typography>
    </Box>
  );

  const renderSignInRequired = () => (
    <Box
      sx={{
        p: "var(--spacing-4x)",
        borderRadius: "var(--spacing-3x)",
        bgcolor: "var(--card-dark-surface)",
        border: "var(--stroke-xs) solid var(--card-dark-border)",
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "50%",
          mx: "auto",
          mb: "var(--spacing-2x)",
          display: "grid",
          placeItems: "center",
          bgcolor: "var(--card-dark-accent-bg)",
          border: "var(--stroke-xs) solid var(--card-dark-border)",
        }}
      >
        <LockOutlinedIcon sx={{ fontSize: 22, color: "var(--card-dark-text-muted)" }} />
      </Box>
      <Typography
        sx={{ font: "var(--semibold-sm)", color: "var(--card-dark-text)", mb: "var(--spacing-1x)" }}
      >
        {t("deck_summary_sign_in_title")}
      </Typography>
      <Typography
        sx={{
          font: "var(--body-xxs)",
          color: "var(--card-dark-text-secondary)",
          lineHeight: 1.5,
          mb: onSignInClick ? "var(--spacing-3x)" : 0,
        }}
      >
        {t("deck_summary_sign_in_body")}
      </Typography>
      {onSignInClick ? (
        <Button
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            onSignInClick();
          }}
          sx={{
            borderRadius: "var(--spacing-2x)",
            px: 2.5,
            textTransform: "none",
            fontWeight: 700,
            bgcolor: "var(--card-dark-accent)",
            color: "#1a1410",
            "&:hover": { bgcolor: "var(--card-dark-text-muted)" },
          }}
        >
          {t("sign_in")}
        </Button>
      ) : null}
    </Box>
  );

  const renderPurchaseRequired = () => (
    <Box
      sx={{
        p: "var(--spacing-4x)",
        borderRadius: "var(--spacing-3x)",
        bgcolor: "#241C18",
        border: "var(--stroke-xs) solid var(--card-dark-border)",
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "50%",
          mx: "auto",
          mb: "var(--spacing-2x)",
          display: "grid",
          placeItems: "center",
          bgcolor: "var(--card-dark-accent-bg)",
          border: "var(--stroke-xs) solid var(--card-dark-border)",
        }}
      >
        <ShoppingBagOutlinedIcon sx={{ fontSize: 22, color: "var(--card-dark-text-muted)" }} />
      </Box>
      <Typography
        sx={{ font: "var(--semibold-sm)", color: "var(--card-dark-text)", mb: "var(--spacing-1x)" }}
      >
        {t("deck_summary_purchase_title")}
      </Typography>
      <Typography
        sx={{
          font: "var(--body-xs)",
          color: "#807356",
          lineHeight: 1.45,
          mb: onPurchaseClick ? "var(--spacing-3x)" : 0,
        }}
      >
        {t("deck_summary_purchase_body_short")}
      </Typography>
      {onPurchaseClick ? (
        <Button
          size="small"
          variant="outlined"
          onClick={(event) => {
            event.stopPropagation();
            onPurchaseClick();
          }}
          sx={{
            borderRadius: "5px",
            px: 2.5,
            textTransform: "none",
            fontWeight: 700,
            color: "var(--card-dark-text)",
            borderColor: "var(--card-dark-border-active)",
            "&:hover": {
              borderColor: "var(--card-dark-accent)",
              bgcolor: "var(--card-dark-accent-bg)",
            },
          }}
        >
          {t("deck_summary_purchase_action")}
        </Button>
      ) : null}
    </Box>
  );

  const renderGoToHallButton = () =>
    talentId ? (
      <Button
        fullWidth
        variant="outlined"
        endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: "1rem !important" }} />}
        onClick={handleGoToHall}
        sx={{
          mt: "var(--spacing-2x)",
          borderRadius: 1.5,
          py: "var(--spacing-2x)",
          textTransform: "none",
          font: "var(--semibold-xs)",
          bgcolor: "#241D13",
          color: "var(--card-dark-text)",
          borderColor: "var(--card-dark-border-active)",
          "&:hover": {
            borderColor: "var(--card-dark-accent)",
            bgcolor: "var(--card-dark-accent-bg)",
          },
        }}
      >
        {t("deck_summary_go_to_talents_hall")}
      </Button>
    ) : null;

  const Backface = () => {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          aspectRatio: CARD_ASPECT_RATIO_CSS,
          borderRadius: scaledCardPx(CARD_BORDER_RADIUS, scale),
          overflow: "hidden",
          bgcolor: CARD_BACK_BG,
          border: "1px solid var(--primary-600)",
          display: "flex",
          flexDirection: "column",
          ...(disableScroll ? { touchAction: "none" } : {}),
        }}
      >
        <Box
          sx={{
            ...cardBackScrollSx,
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            touchAction: "pan-y",
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
            px: "var(--spacing-4x)",
            pt: "var(--spacing-4x)",
            pb: "var(--spacing-3x)",
          }}
        >
          <Stack spacing="var(--spacing-3x)">
            {/* Header */}
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Typography sx={sectionLabelSx}>{t("deck_summary_title")}</Typography>
                {shareable && onShare && isLoggedIn ? (
                  <Button
                    size="small"
                    startIcon={<Share sx={{ fontSize: "0.95rem !important" }} />}
                    onClick={(event) => {
                      event.stopPropagation();
                      onShare();
                    }}
                    sx={{
                      flexShrink: 0,
                      minWidth: 0,
                      borderRadius: "999px",
                      px: 1.25,
                      py: 0.35,
                      textTransform: "none",
                      font: "var(--semibold-xxs)",
                      color: "var(--card-dark-text-muted)",
                      border: "var(--stroke-xs) solid var(--card-dark-border-active)",
                      "&:hover": {
                        bgcolor: "var(--card-dark-accent-bg)",
                        borderColor: "var(--card-dark-accent)",
                      },
                    }}
                  >
                    {t("share_card_action")}
                  </Button>
                ) : null}
              </Stack>

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                {talentName ? (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing="var(--spacing-2x)"
                    sx={{ mt: "var(--spacing-1x)" }}
                  >
                    <Avatar
                      src={talentAvatarUrl || undefined}
                      alt={talentName}
                      sx={{
                        width: 34,
                        height: 34,
                        border: "var(--stroke-xs) solid var(--card-dark-border)",
                      }}
                    >
                      {talentName.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography
                      sx={{
                        font: "var(--semibold-sm)",
                        color: "var(--card-dark-text)",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      {talentName}
                    </Typography>
                  </Stack>
                ) : null}
                {purchaseId ? (
                  <Stack
                    alignItems="flex-end"
                    spacing="var(--spacing-1x)"
                    sx={{ flexShrink: 0, mt: 1 }}
                  >
                    <Button
                      size="small"
                      disabled={isDownloading}
                      startIcon={
                        isDownloading ? (
                          <CircularProgress
                            size={12}
                            thickness={5}
                            sx={{ color: "var(--primary-bg-dark)" }}
                          />
                        ) : (
                          <DownloadIcon size={14} />
                        )
                      }
                      onClick={(event) => {
                        event.stopPropagation();
                        onDownloadClick?.();
                      }}
                      sx={{
                        minWidth: 0,
                        borderRadius: "var(--spacing-2x)",
                        px: 1.5,
                        py: 0.5,
                        textTransform: "none",
                        font: "var(--semibold-xxs)",
                        background: "var(--button-gradient)",
                        color: "var(--primary-bg-dark)",
                        boxShadow: "none",
                        border: "none",
                        "&:hover": {
                          background: "var(--button-gradient-hover)",
                          boxShadow: "none",
                        },
                        "&.Mui-disabled": {
                          background: "var(--button-gradient-disabled)",
                          color: "var(--primary-bg-dark)",
                          opacity: 0.75,
                        },
                      }}
                    >
                      {isDownloading ? "Preparing…" : "Download card"}
                    </Button>
                    {isDownloading ? (
                      <Typography
                        sx={{
                          font: "var(--body-xxs)",
                          color: "var(--card-dark-text-muted)",
                          textAlign: "right",
                          maxWidth: 148,
                          lineHeight: 1.35,
                        }}
                      >
                        Your card will download shortly
                      </Typography>
                    ) : null}
                  </Stack>
                ) : null}
              </Stack>
            </Box>

            {isCollection ? (
              <Box sx={{ ...benefitTileSx, textAlign: "center", py: "var(--spacing-5x)" }}>
                <Typography
                  sx={{ font: "var(--body-xs)", color: "var(--card-dark-text-secondary)" }}
                >
                  {description ?? t("no_description_available")}
                </Typography>
              </Box>
            ) : (
              <>
                {/* Card type + tagline */}
                <Box>
                  <Typography
                    sx={{
                      font: "var(--semibold-xs)",
                      color: "var(--card-dark-text-muted)",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      pb: "var(--spacing-1x)",
                      borderBottom: "2px solid var(--card-dark-border-active)",
                      display: "inline-block",
                    }}
                  >
                    {t(cardInfo.labelKey)}
                  </Typography>
                  <Typography
                    sx={{
                      mt: "var(--spacing-2x)",
                      font: "var(--body-xs)",
                      color: "var(--card-dark-text)",
                      lineHeight: 1.5,
                      fontWeight: 300,
                    }}
                  >
                    {cardSubtitle}
                  </Typography>
                </Box>

                {/* What's inside */}
                {showPreviewSections ? (
                  <Box pt={1}>
                    <Typography sx={sectionLabelSx}>
                      {t("deck_summary_whats_inside_card")}
                    </Typography>
                    <Stack spacing="var(--spacing-2x)">
                      {cardInfo.benefits.map(renderBenefitTile)}
                    </Stack>
                  </Box>
                ) : null}

                {showLivingPlan && livingPlan.length > 0 ? (
                  <Box pt={1}>
                    <Typography sx={sectionLabelSx}>{t("deck_summary_living_plan")}</Typography>
                    <Stack spacing="var(--spacing-2x)">
                      {livingPlan.map(renderLivingPlanTile)}
                    </Stack>
                  </Box>
                ) : null}
              </>
            )}

            {/* Bottom engagement / access gate */}
            {!isCollection ? (
              !isLoggedIn ? (
                renderSignInRequired()
              ) : !isOwner && !isPurchased ? (
                renderPurchaseRequired()
              ) : (
                <Stack spacing="var(--spacing-3x)">
                  {isOwner ? (
                    <CardEngagementAccordion
                      cardId={cardId}
                      cardVisits={summary?.clicked_count ?? 0}
                      contentViews={summary?.viewed_count ?? 0}
                    />
                  ) : null}
                  {canViewEngagement ? (
                    <CardEngagementPanel summary={summary} viewerUsername={userInfo?.username} />
                  ) : null}
                </Stack>
              )
            ) : null}

            {renderGoToHallButton()}
          </Stack>
        </Box>
      </Box>
    );
  };

  return addBorder ? (
    <Box sx={{ borderRadius: scaledCardPx(CARD_BORDER_RADIUS, scale) }}>
      <CardBorder>
        <Backface />
      </CardBorder>
    </Box>
  ) : (
    <Backface />
  );
};

export default DeckCardExpandBackFace;
